import { z } from 'zod';

/**
 * Shared types and schemas for tRPC. Single source of truth for API contract types.
 * Import from @my-fullstack-app/trpc-types in apps/mobile and apps/api.
 */

export const healthCheckSchema = z.object({
  ok: z.boolean(),
  timestamp: z.string(),
});

export type HealthCheck = z.infer<typeof healthCheckSchema>;

// Password bounds for account creation / changes. Min 8 is the NIST floor.
// The max is enforced on BYTE length, not character count: bcrypt truncates
// at 72 *bytes*, so a `z.string().max(72)` (which counts UTF-16 code units)
// would still let a 72-char multibyte password (up to ~288 bytes) be
// silently truncated — the exact footgun. `TextEncoder` gives the UTF-8 byte
// length and exists on React Native, web, and Node alike. Login is
// intentionally exempt from these bounds — validating length there adds no
// security (the hash comparison is authoritative) and a min would lock out
// any pre-existing shorter password.
export const PASSWORD_MIN = 8;
export const PASSWORD_MAX_BYTES = 72;

export const newPasswordSchema = z
  .string()
  .min(PASSWORD_MIN)
  .refine((pw) => new TextEncoder().encode(pw).length <= PASSWORD_MAX_BYTES, {
    message: `Password must be at most ${PASSWORD_MAX_BYTES} bytes`,
  });

export const authSchemas = {
  signup: z.object({
    email: z.string().email(),
    password: newPasswordSchema,
  }),
  login: z.object({
    email: z.string().email(),
    password: z.string().min(1),
  }),
  refresh: z.object({
    refreshToken: z.string().min(10),
  }),
};

/**
 * Contact-us / bug-report submissions (ADR-0070). `contact.submit` is a
 * public procedure — anyone, signed in or not, can send feedback or
 * report a problem — so `email` is always required as the reply channel.
 * `diagnostics` is a small, non-PII context bag the client fills in
 * automatically (platform, app version, locale, timezone).
 */
export const contactSchemas = {
  submit: z.object({
    type: z.enum(['bug', 'feedback', 'question', 'business-waitlist', 'other']),
    subject: z.string().trim().min(1).max(150),
    message: z.string().trim().min(10).max(5000),
    email: z.string().email().max(254),
    location: z.string().trim().max(200).optional(),
    diagnostics: z
      .object({
        platform: z.string().max(40).optional(),
        appVersion: z.string().max(40).optional(),
        locale: z.string().max(40).optional(),
        timezone: z.string().max(80).optional(),
      })
      .optional(),
  }),
};

export type ContactSubmissionInput = z.infer<typeof contactSchemas.submit>;
export type ContactType = ContactSubmissionInput['type'];

export function trpcTypes(): string {
  return 'trpc-types';
}

export const shareRoleSchema = z.enum(['admin', 'editor', 'viewer']);
export type ShareRoleEnum = z.infer<typeof shareRoleSchema>;

// Weather forecast — types live here (not in apps/api) so the mobile app can
// import them without crossing app boundaries. The API's WeatherProvider
// implementations consume the same shapes (re-exported there for symmetry).
export type WeatherCondition =
  | 'clear'
  | 'clouds'
  | 'rain'
  | 'snow'
  | 'storm'
  | 'fog'
  | 'other';

export interface HourlyBucket {
  /** ISO datetime in the location's local timezone. */
  time: string;
  tempC: number;
  condition: WeatherCondition;
  /** 0..1 — probability of precipitation in this 3-hour window. */
  precipitationProb: number;
  /** Vendor-native icon code, kept for debug. */
  iconCode: string;
}

export interface DailyForecast {
  /** ISO yyyy-MM-dd in the location's local timezone. */
  date: string;
  tempMinC: number;
  tempMaxC: number;
  /** Dominant condition across the day. */
  condition: WeatherCondition;
  /** Max precipitation probability across the day. */
  precipitationProb: number;
  iconCode: string;
  /** Per-3-hour buckets for this date. */
  buckets: HourlyBucket[];
}

export type ForecastResponse = Record<string, DailyForecast | null>;

// Nearby places + suggestions — types live here (not apps/api) so mobile
// can import them without crossing app boundaries.
export interface NearbyInput {
  centerLat: number;
  centerLng: number;
  /** Search radius in meters. Default 5000 (5 km). */
  radiusMeters?: number | undefined;
  /** Google Places types to query. Provider may issue one call per type and merge. */
  types?: string[] | undefined;
  /** Place IDs to filter out (e.g. already in the trip). */
  excludePlaceIds?: string[] | undefined;
  /** Hard cap on results returned by the provider (after dedup). Default 20. */
  limit?: number | undefined;
}

/** Photo author attribution per Google Maps Platform ToS §3.2.3 —
 *  must be displayed on every Place Photos surface. Mirrors the shape
 *  in `@my-fullstack-app/itinerary-types`; duplicated structurally
 *  because libs are forbidden from cross-importing (see CLAUDE.md). */
export interface PhotoAttribution {
  displayName: string;
  uri?: string | undefined;
}

/**
 * Width ladder for the Place Photos proxy (`/api/places/photo`, ADR-0034).
 *
 * The proxy's cache key — and the billable Google Place Photos call it
 * guards — is `<placeId>:<photoRef>:<maxWidthPx>`. Call sites derive the
 * width from live viewport / container dimensions (`Math.ceil(width)`,
 * `viewportWidth * 2`, …), so an *unquantized* width turns every distinct
 * pixel value — every browser size, every window resize — into a fresh
 * cache key and a fresh billable lookup. That fragmentation defeats the
 * L1 / L2 / browser / CDN caches and inflates the v1 Place Details Photos
 * SKU: a 1366-px and a 1440-px desktop bill the same photo twice.
 *
 * Snapping each request to a small ladder collapses that cardinality to at
 * most four buckets per (placeId, photoRef). The rungs span thumbnail
 * (400) → row (800) → retina row (1200) → full-bleed hero (1600, the
 * proxy ceiling). 1600 is the cap because Google bills the same regardless
 * of resolution and larger gives no visible benefit on the screens we
 * target.
 */
export const PHOTO_WIDTH_BUCKETS = [400, 800, 1200, 1600] as const;
export type PhotoWidthBucket = (typeof PHOTO_WIDTH_BUCKETS)[number];

/**
 * Snaps an arbitrary requested width UP to the smallest
 * `PHOTO_WIDTH_BUCKETS` rung >= the request, capping at the largest rung.
 * Snapping up (never down) guarantees we never hand back a blurrier image
 * than asked for. Non-finite / non-positive input falls back to the 1200
 * rung (the proxy's historical default).
 *
 * Shared by the client URL builder (so the proxy URL is identical across
 * users at similar sizes → browser + CDN cache hits) and the server
 * resolver (so the cache key + Google call cardinality stays bounded no
 * matter what `?w=` arrives). Idempotent: re-quantizing a bucket returns
 * the same bucket.
 */
export function quantizePhotoWidth(requested: number | undefined): PhotoWidthBucket {
  if (requested == null || !Number.isFinite(requested) || requested <= 0) return 1200;
  // First rung >= the request; anything above the top rung caps at the
  // ceiling (1600 — the proxy max, and the last bucket).
  return PHOTO_WIDTH_BUCKETS.find((bucket) => requested <= bucket) ?? 1600;
}

/** @deprecated ADR-0044 — prefer `PlaceCatalogEntry`. Internal API
 *  shape returned by the legacy nearby/suggestion routes; will be
 *  removed once those routes are. */
export interface NearbyPlace {
  placeId: string;
  name: string;
  types: string[];
  lat: number;
  lng: number;
  photoRef?: string | undefined;
  rating?: number | undefined;
  userRatingsTotal?: number | undefined;
  priceLevel?: number | undefined;
  /** First-attribution for the photo at `photoRef` (ToS §3.2.3). */
  photoAttribution?: PhotoAttribution | undefined;
}

/** @deprecated ADR-0044 — prefer `SuggestedCatalogPlace`. Removed once
 *  all consumers migrate. */
export interface SuggestedPlace extends NearbyPlace {
  /** Server-computed score 0..1. The router pre-sorts; clients can re-sort if needed. */
  score: number;
  /** Short reason chips for the UI ("nearby", "matches your culture interest"). */
  reasons: string[];
}

export interface SuggestionContext {
  centerLat: number;
  centerLng: number;
  existingPlaceIds: string[];
  /** Union of `types[]` across the trip's existing places. */
  existingTypes: string[];
  /** TripPreferences.interests (history / culture / food / nature / nightlife / shopping). */
  interests: string[];
}

// ─── ADR-0044: Vendor-neutral place catalog ─────────────────────────────
//
// Provider-neutral types that replace the Google-shaped surface
// (PlaceAutocompletePrediction, PlaceDetails, NearbyPlace) over the
// next migration. Callers consume only these; each backend provider
// (Google, Mapbox, OSM, curated, manual) adapts its native response.

/** Which catalog backend produced an entry. Encoded into `id` via the
 *  '<source>:<native>' convention so the client can dispatch lookup /
 *  photo calls without re-querying the server for routing info. */
export type PlaceCatalogSource =
  | 'google'
  | 'mapbox'
  | 'osm'
  | 'curated'
  | 'manual';

/** Normalized category vocabulary. Each adapter maps its native
 *  taxonomy into one of these. New categories are a one-line addition;
 *  free-form strings would silently produce vendor-named long tails
 *  ("street_address", "tourist_attraction"). */
export type PlaceCategory =
  | 'lodging'
  | 'food'
  | 'cafe'
  | 'museum'
  | 'attraction'
  | 'beach'
  | 'park'
  | 'viewpoint'
  | 'transport'
  | 'airport'
  | 'station'
  | 'shopping'
  | 'nightlife'
  | 'service'
  | 'other';

/**
 * Provider-neutral place result. Every catalog source adapts its
 * native response into this shape. Frontend code never branches on
 * `source` for rendering; it's there for analytics + dedup ranking.
 *
 * `id` is `'<source>:<native-id>'` (e.g. `'google:ChIJN...'`) — treat
 * it as atomic on the client. Only the registry parses the prefix to
 * dispatch follow-up calls.
 */
export interface PlaceCatalogEntry {
  id: string;
  source: PlaceCatalogSource;
  name: string;
  address?: string | undefined;
  lat?: number | undefined;
  lng?: number | undefined;
  /** International phone (free-form display string). Pro-tier —
   *  populated when the Place Details request includes the
   *  `contact` feature. */
  phone?: string | undefined;
  /** Official website URL. Pro-tier — bundled with `phone` under the
   *  `contact` feature. */
  website?: string | undefined;
  category?: PlaceCategory | undefined;
  /** Opaque token for the photo proxy. May be absent — not every
   *  entry has a photo. The client passes this back to
   *  `/api/places/photo?token=...&w=...` and gets a 302. */
  photoToken?: string | undefined;
  /** First-attribution snapshot for the photo behind `photoToken`
   *  (ToS §3.2.3). Frontend renders this caption beside every place
   *  photo it shows. */
  photoAttribution?: PhotoAttribution | undefined;
  rating?: number | undefined;
  ratingCount?: number | undefined;
  priceTier?: 1 | 2 | 3 | 4 | undefined;
  openingHours?: string[] | undefined;
  /** ISO 3166-1 alpha-2 country code, derived server-side from the
   *  tail of `address` via REST Countries (`lookupCountry`). Optional —
   *  null when the address didn't end in a recognisable country name
   *  (legacy minimal addresses, very partial Pro-tier reverse-geocodes).
   *  See ADR-0054. */
  countryCode?: string | undefined;
}

/** Slim row returned by autocomplete. */
export interface PlaceCatalogPrediction {
  id: string;
  source: PlaceCatalogSource;
  /** Primary text (e.g. "Hotel Fasano"). */
  title: string;
  /** Secondary text (e.g. "Rio de Janeiro, Brazil"). */
  subtitle?: string | undefined;
}

/**
 * Ranked catalog entry for the suggestions surface. Same fields as
 * PlaceCatalogEntry plus the server-computed score + human-readable
 * reason chips ("nearby", "matches your culture interest"). The
 * neutral counterpart of the legacy SuggestedPlace.
 */
export interface SuggestedCatalogPlace extends PlaceCatalogEntry {
  /** 0..1, server-computed. The router pre-sorts; clients can re-sort. */
  score: number;
  /** Short reason chips for the UI ("nearby", "matches your culture interest"). */
  reasons: string[];
}

// ─── ADR-0061: AI itinerary generation ─────────────────────────────────
//
// Wire shape for `ai.generateItinerary`. The schemas live here (not in
// apps/api) so the mobile app can build form payloads against the same
// definition the server validates. The model-output schema (what Claude
// emits via tool_use) is server-only — it lives in
// apps/api/src/trpc/schemas/ai-itinerary-output.ts.

/** Subset of `PlaceRef` (libs/itinerary-types) that the AI input needs.
 *  Kept minimal so the AI surface doesn't churn every time PlaceRef gains
 *  a field. Mirrors the picker output shape used by TripForm. */
export const aiPlaceRefSchema = z.object({
  id: z.string().min(1).max(255),
  name: z.string().min(1).max(255),
  address: z.string().max(500).optional(),
  lat: z.number().min(-90).max(90).optional(),
  lng: z.number().min(-180).max(180).optional(),
  countryCode: z.string().regex(/^[A-Z]{2}$/).optional(),
});

export const aiInterestSchema = z.enum([
  'history',
  'culture',
  'food',
  'nature',
  'nightlife',
  'shopping',
]);
export type AiInterest = z.infer<typeof aiInterestSchema>;

/** Form payload for a draft generation. Day count is derived from the
 *  date range server-side; we don't trust a client-supplied day count.
 *  `freeText` is the "Anything specific?" field (capped at 500 chars per
 *  ADR-0061). */
export const generationInputSchema = z.object({
  destinationPlace: aiPlaceRefSchema,
  startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  preferences: z.object({
    interests: z.array(aiInterestSchema).max(6).default([]),
    active: z.boolean().optional(),
    relaxed: z.boolean().optional(),
    kids: z.boolean().optional(),
  }),
  freeText: z.string().max(500).optional(),
});
export type GenerationInput = z.infer<typeof generationInputSchema>;

/** Draft place emitted by the model. Resolved to a real PlaceRef
 *  client-side via `places.autocomplete` at user-accept time. */
export interface DraftPlace {
  /** Model-suggested display name ("Time Out Market", "Pastéis de Belém"). */
  name: string;
  /** Query string passed to autocomplete on resolve. Often the same as
   *  `name` but the model may include disambiguating context
   *  ("Time Out Market Lisbon"). */
  suggestedQuery: string;
  /** Neighbourhood / area string ("Alfama", "Bairro Alto"). UI hint
   *  only; not persisted as-is. */
  area?: string | undefined;
  /** Model-emitted coords. Used only as a hint to bias autocomplete;
   *  the resolved place's coords win on persist. */
  lat?: number | undefined;
  lng?: number | undefined;
  /** 'HH:mm' wall-clock. */
  startTime: string;
  endTime: string;
  /** Coarse vocabulary the model uses for visual grouping in the draft
   *  review screen. Maps onto `Place.types[]` heuristically on resolve. */
  category?:
    | 'restaurant'
    | 'cafe'
    | 'bar'
    | 'museum'
    | 'gallery'
    | 'park'
    | 'landmark'
    | 'shopping'
    | 'activity'
    | 'other' | undefined;
  /** One-sentence rationale ("classic Alfama fado dinner — book ahead"). */
  why?: string | undefined;
}

/** Draft custom entry — "Free time", "Hotel check-in", "Lunch break". */
export interface DraftCustom {
  title: string;
  subtitle?: string | undefined;
  startTime: string;
  endTime?: string | undefined;
  notes?: string | undefined;
}

/** A draft day as emitted by the model. No id — minted client-side
 *  on accept. */
export interface DraftDay {
  /** 'YYYY-MM-DD' — must fall inside the trip's date range. */
  date: string;
  /** Optional theme label ("Old Town & Fado", "Beach day"). */
  label?: string | undefined;
  places: DraftPlace[];
  customs: DraftCustom[];
}

/** Stream event sent to the client during a generation. The terminal
 *  event is either `done` (success) or `error`. */
export type GenerationEvent =
  | {
      kind: 'meta';
      tripName: string;
      summary: string;
      dayCount: number;
    }
  | {
      kind: 'day';
      day: DraftDay;
    }
  | {
      kind: 'reference';
      /** ADR-0060 — surfaces auto-suggested maps / transit guides
       *  alongside the draft, dropped as `TripReference` rows on
       *  accept. v1 is "name + url + kind" only; richer metadata can
       *  follow without a schema rev. */
      ref: {
        kind: 'map' | 'transit' | 'guide' | 'visa' | 'embassy';
        title: string;
        url: string;
      };
    }
  | {
      kind: 'done';
      tripName: string;
      summary: string;
      days: DraftDay[];
    }
  | {
      kind: 'error';
      code:
        | 'QUOTA_EXCEEDED'
        | 'AI_UNAVAILABLE'
        | 'INPUT_INVALID'
        | 'RATE_LIMITED'
        | 'PROVIDER_ERROR';
      message: string;
    };

/** A single quota bucket. Two of these are returned per call to
 *  `ai.quotaStatus` — one for itinerary generation, one for the
 *  booking-confirmation parser (ADR-0081). */
export interface AiQuotaBucket {
  used: number;
  cap: number;
  /** 'YYYY-MM-DD' of the first day of the next month — when the quota
   *  rolls over. */
  resetsAt: string;
  tier: 'basic' | 'pro' | string;
}

/** Read shape for `ai.quotaStatus`. Per ADR-0081 + ADR-0082 + ADR-0083
 *  the response is split into five buckets so the mobile UI can gate
 *  each AI surface independently:
 *
 *  - `generate` — ADR-0061 itinerary generation
 *  - `parse` — ADR-0081 booking-confirmation parser
 *  - `chat` — ADR-0082 embedded trip assistant: one debit per user-sent
 *    chat message
 *  - `chatVision` — ADR-0082: one debit per successful
 *    `vault.readDocument` tool call (vision-token sub-budget)
 *  - `extract` — ADR-0083 vault document extraction (passport, visa,
 *    insurance, vaccination, …)
 *
 *  All five buckets always present; a kill-switched or non-applicable
 *  bucket reports `cap: 0`. */
export interface AiQuotaStatus {
  generate: AiQuotaBucket;
  parse: AiQuotaBucket;
  chat: AiQuotaBucket;
  chatVision: AiQuotaBucket;
  extract: AiQuotaBucket;
}

// ─── ADR-0081: AI booking-confirmation parser ──────────────────────────
//
// Wire shape for `ai.parseBookingFromAttachment`. The parser reads a
// vault attachment (PDF / image / .eml) via R2 presigned GET, routes it
// through Claude with three tools (`submit_flight_booking`,
// `submit_hotel_booking`, `submit_unknown`), and returns one of:
//   - { kind: 'flight', bookings: FlightLegExtract[] }
//   - { kind: 'hotel', booking: HotelExtract }
//   - { kind: 'unknown', reason: string }
//   - { kind: 'error', code, message }
//
// Mobile renders the result in a state-machine review modal; on accept
// the adapter mints Journey / Stay entries via the existing slices.

/** Origin / destination anchor on a flight/train/bus booking. Mirrors
 *  `JourneyAnchor` (libs/itinerary-types) but narrowed to what the
 *  model can extract from a confirmation document. `code` is optional
 *  because not every transit mode uses one; the server validates the
 *  format `^[A-Z]{2,3}$` and silently drops malformed codes. */
export interface BookingAnchorExtract {
  /** Display name as the model reads it from the document.
   *  e.g. "John F. Kennedy International Airport", "Paris Gare du Nord". */
  name: string;
  /** IATA (3-letter) or station (2-letter) code. Regex-validated server-
   *  side before reaching the client; malformed codes are dropped, not
   *  re-prompted. */
  code?: string | undefined;
  terminal?: string | undefined;
  gate?: string | undefined;
  platform?: string | undefined;
  /** Scheduled wall-clock time as the document states it. Either an
   *  ISO datetime with offset ('2026-10-21T23:00-04:00') or a naive
   *  wall-clock ('2026-10-21T23:00') — the latter requires
   *  `timezoneIana` to be unambiguous; the mobile fix-up path resolves
   *  it once the user picks the origin place. */
  timeIso: string;
  /** IANA timezone name ('America/New_York'). Advisory — the model
   *  fills it in when it can infer one from an airport code (the
   *  few-shot pins the top 50). Manual fix-up handles the long tail. */
  timezoneIana?: string | undefined;
}

/** A single leg extracted from a flight (or train/bus/ferry/transfer)
 *  confirmation. Multi-leg PDFs (typical for return tickets, segmented
 *  international flights) produce N legs that share `bookingRef` and
 *  `confirmationCode`. */
export interface FlightLegExtract {
  mode: 'flight' | 'train' | 'bus' | 'ferry' | 'transfer';
  /** Operating carrier name, free-form ("American Airlines", "Eurostar"). */
  carrier: string;
  /** Flight / train number ("AA245", "ES9020"). Omitted only when the
   *  document is genuinely missing it (very rare). */
  serviceNumber?: string | undefined;
  /** Booking reference / PNR. Six-character alphanumerics are typical
   *  but the model preserves whatever the document shows. */
  bookingRef?: string | undefined;
  /** Confirmation code, when distinct from the PNR. */
  confirmationCode?: string | undefined;
  origin: BookingAnchorExtract;
  destination: BookingAnchorExtract;
  seat?: string | undefined;
  travelClass?: string | undefined;
  pricePaid?: { amount: number; currency: string } | undefined;
  /** Model-self-reported confidence per leg. UI surfaces this as a
   *  chip on the review card; 'low' reveals a warning banner ("verify
   *  against the original"). Not calibrated — treat as a UX hint. */
  confidence: 'high' | 'medium' | 'low';
}

/** A hotel / lodging extract. Single instance per call (one tool_use
 *  per document); a single PDF with both flight and hotel produces
 *  whichever the model classifies as the dominant content. */
export interface HotelExtract {
  /** Hotel / property display name. */
  name: string;
  /** Raw address as the document states it. Geocoded client-side at
   *  accept time via `places.search` to mint the Stay's `PlaceRef`. */
  address?: string | undefined;
  /** ISO datetime, '2026-10-21T15:00'. */
  checkIn: string;
  /** ISO datetime. */
  checkOut: string;
  /** IANA timezone for check-in / check-out. Advisory; the mobile
   *  fix-up path resolves it from the geocoded place's lat/lng if
   *  absent. */
  timezoneIana?: string | undefined;
  roomType?: string | undefined;
  unitNumber?: string | undefined;
  bookingRef?: string | undefined;
  confirmationCode?: string | undefined;
  /** Booking channel — "Booking.com", "Marriott direct", "Expedia". */
  provider?: string | undefined;
  pricePaid?: { amount: number; currency: string } | undefined;
  confidence: 'high' | 'medium' | 'low';
}

/** Discriminated union returned by `ai.parseBookingFromAttachment`.
 *  Mobile renders one card per `kind`. */
export type ParseResult =
  | { kind: 'flight'; bookings: FlightLegExtract[] }
  | { kind: 'hotel'; booking: HotelExtract }
  | { kind: 'unknown'; reason: string }
  | {
      kind: 'error';
      code:
        | 'QUOTA_EXCEEDED'
        | 'AI_UNAVAILABLE'
        | 'FORBIDDEN_MIME'
        | 'ATTACHMENT_NOT_FOUND'
        | 'RATE_LIMITED'
        | 'EXTRACT_FAILED'
        | 'PROVIDER_ERROR';
      message: string;
    };

/** Wire input for `ai.parseBookingFromAttachment`. Both ids are
 *  required; the trip access check + attachment lookup happen
 *  server-side. */
export const parseBookingInputSchema = z.object({
  tripId: z.string().min(1).max(255),
  attachmentId: z.string().min(1).max(255),
});
export type ParseBookingInput = z.infer<typeof parseBookingInputSchema>;

/** MIME types the mobile "Extract booking" action is shown for.
 *  Narrower than the API's server-side allow-list (which also accepts
 *  HEIC / HEIF defensively, then short-circuits at the provider since
 *  Anthropic vision rejects them). The action is hidden for HEIC /
 *  HEIF to avoid an "always fails" affordance — users keep the HEIC
 *  in their vault and re-upload a JPEG if they want to parse it. */
export const PARSER_ELIGIBLE_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'message/rfc822',
] as const;
export type ParserEligibleMimeType = (typeof PARSER_ELIGIBLE_MIME_TYPES)[number];
export function isParserEligibleMime(mime: string): mime is ParserEligibleMimeType {
  return (PARSER_ELIGIBLE_MIME_TYPES as readonly string[]).includes(mime);
}

// ─── ADR-0083: AI vault document extraction ────────────────────────────
//
// Wire shapes for `ai.extractVaultDocument` (vision pass → structured
// fields) and `ai.confirmExtractedDocument` (user-confirmed fields
// persist on `Attachment.extracted`). The extractor is schema-driven —
// the field set per label lives in `libs/itinerary-types/document-schemas.ts`
// and the provider builds Claude tools / Zod validators / mobile forms
// from that registry.
//
// MIME types the mobile "Extract details" action is shown for. Narrower
// than the booking parser allow-list because `.eml` doesn't apply to
// passport / visa / insurance / vaccination scans.

export const EXTRACTOR_ELIGIBLE_MIME_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
] as const;
export type ExtractorEligibleMimeType = (typeof EXTRACTOR_ELIGIBLE_MIME_TYPES)[number];
export function isExtractorEligibleMime(mime: string): mime is ExtractorEligibleMimeType {
  return (EXTRACTOR_ELIGIBLE_MIME_TYPES as readonly string[]).includes(mime);
}

/** Wire input for `ai.extractVaultDocument`. The attachment's `label`
 *  determines which `DocumentSchema` drives the extraction; if there's
 *  no registered schema for that label, the server returns
 *  `{ kind: 'error', code: 'UNSUPPORTED_LABEL' }`. */
export const extractVaultDocumentInputSchema = z.object({
  tripId: z.string().min(1).max(255),
  attachmentId: z.string().min(1).max(255),
});
export type ExtractVaultDocumentInput = z.infer<typeof extractVaultDocumentInputSchema>;

/** Field-value union sent over the wire. Mirrors
 *  `ExtractedFieldValue` in itinerary-types — Zod's `z.union` covers
 *  string / number / Money / string-array / the 'worldwide' literal. */
// Structural mirror of `ExtractedFieldValue` in itinerary-types (the lib
// boundary forbids importing it here). zod 4 infers a bare `z.ZodTypeAny`
// as `unknown`, so annotate with the explicit union to keep the wire
// field-value type precise and assignable to `ExtractedFieldValue`.
type ExtractedFieldValueWire =
  | string
  | number
  | { amount: number; currency: string }
  | readonly string[]
  | 'worldwide';
const extractedFieldValueSchema: z.ZodType<ExtractedFieldValueWire> = z.union([
  z.string(),
  z.number(),
  z.object({
    amount: z.number().finite().nonnegative(),
    currency: z.string().min(3).max(3),
  }).strict(),
  z.array(z.string()),
  z.literal('worldwide'),
]);

/** Wire shape for an `ExtractedDocument` in `confirmExtractedDocument`
 *  input + the `extracted` return path. The router calls the schema
 *  runtime's `validateExtractedFields` to enforce per-label field types
 *  on top of this surface-level shape. */
export const extractedDocumentSchema = z.object({
  label: z.string().min(1).max(64),
  schemaVersion: z.number().int().min(1).max(1000),
  fields: z.record(z.string(), extractedFieldValueSchema),
  confidenceByField: z
    .record(z.string(), z.enum(['high', 'medium', 'low']))
    .optional(),
  // Must be a parseable ISO 8601 datetime — server-side
  // `confirmExtractedDocument` persists this verbatim, and the
  // assistant's "based on your X uploaded May 12, …" attribution
  // depends on `new Date(extractedAt)` returning a valid Date. Wire
  // validation here means a malformed string never reaches DynamoDB.
  extractedAt: z.string().datetime(),
  extractedModel: z.enum(['sonnet-4-6', 'haiku-4-5']),
});
export type ExtractedDocumentWire = z.infer<typeof extractedDocumentSchema>;

/** Sentinel prefix for the BAD_REQUEST message `ai.confirmExtractedDocument`
 *  emits when `input.document.schemaVersion > schema.schemaVersion`
 *  (deploy downgrade race). Both sides must reference this constant —
 *  the server prepends it to the user-facing message and the mobile
 *  client uses it via `startsWith` to route to the retryable
 *  re-extract phase. Decoupling the discriminator from the
 *  user-facing text would require a custom TRPCError formatter that
 *  attaches a typed code to `data`; this shared constant is the
 *  lightweight version. See ADR-0083 §Q13. */
export const SCHEMA_VERSION_MISMATCH_PREFIX =
  'Document was extracted under schema version';

/** Wire input for `ai.confirmExtractedDocument`. The user-edited
 *  `document.fields` map is re-validated server-side against the
 *  per-label schema before being persisted on `Attachment.extracted`. */
export const confirmExtractedDocumentInputSchema = z.object({
  tripId: z.string().min(1).max(255),
  attachmentId: z.string().min(1).max(255),
  document: extractedDocumentSchema,
});
export type ConfirmExtractedDocumentInput = z.infer<
  typeof confirmExtractedDocumentInputSchema
>;

/** Discriminated union returned by `ai.extractVaultDocument`. The
 *  `extracted` variant carries an unconfirmed `ExtractedDocument` the
 *  mobile review screen edits and submits via
 *  `ai.confirmExtractedDocument`. */
export type ExtractVaultDocumentResult =
  | { kind: 'extracted'; document: ExtractedDocumentWire }
  | { kind: 'unknown'; reason: string }
  | {
      kind: 'error';
      code:
        | 'QUOTA_EXCEEDED'
        | 'AI_UNAVAILABLE'
        | 'FORBIDDEN_MIME'
        | 'ATTACHMENT_NOT_FOUND'
        | 'UNSUPPORTED_LABEL'
        | 'RATE_LIMITED'
        | 'EXTRACT_FAILED'
        | 'PROVIDER_ERROR';
      message: string;
    };

// ─── ADR-0082: Embedded trip assistant ─────────────────────────────────
//
// Wire shapes for `ai.sendMessage`, `ai.listConversation`, and
// `ai.clearConversation`. The assistant is a multi-turn chat scoped to
// `(userId, tripId)`; structured tool-use grounds every factual claim in
// real trip entities or vault attachments.
//
// Slice 1 ships these types + the non-streaming `sendMessage` mutation
// backed by NoopAssistantProvider — the wire contract exists end-to-end
// before any tokens are spent.

/** A single citation attached to an assistant turn. References either a
 *  trip entity (`Journey` / `Stay` / `Place` / `CustomEntry`) or a vault
 *  attachment. The mobile UI renders these as tappable chips that
 *  navigate to the cited surface. */
export interface AssistantCitation {
  kind: 'entity' | 'attachment';
  /** Entity id (`Journey.id` / `Stay.id` / …) or `Attachment.id`. */
  id: string;
  /** Short user-facing label shown on the chip ("Lisbon flight",
   *  "Passport (uploaded Feb 14)"). Capped at 80 chars upstream. */
  label: string;
}

/** A single message in an assistant conversation. User messages carry
 *  only `text`; assistant messages may additionally carry `citations`,
 *  `confidence`, and a redacted summary of the tools called to answer. */
export interface AssistantMessage {
  /** Server-minted opaque id (UUID). Ordering is by `createdAt`. */
  id: string;
  role: 'user' | 'assistant';
  text: string;
  /** ISO datetime. */
  createdAt: string;
  // ── assistant-only fields ──────────────────────────────────────────
  citations?: AssistantCitation[] | undefined;
  confidence?: 'high' | 'medium' | 'low' | undefined;
  /** Lightweight ledger of the tools the assistant called for audit /
   *  debug UI. Never includes raw tool output (which can be large or
   *  PII-bearing). */
  toolCalls?:
    | {
        name: string;
        outputSummary: string;
        errored: boolean;
        /** ADR-0084 Tier-A revert payload. Present only on successful
         *  write-tool calls (`notes.set`, `places.update`, …). Carries
         *  the minimum data the client needs to render the chat-Undo
         *  affordance and reach the revert endpoint without a separate
         *  audit-log round-trip. Field NAMES describe the previous
         *  state — never the post-write state, which the client already
         *  sees in its local cache after the hydrate. Slice 1 ships
         *  the `note` discriminant only. */
        revert?:
          | {
              kind: 'note';
              tripId: string;
              entityId: string;
              previousMarkdown: string;
              /** Audit event id stamped by the Slice-2 audit logger.
               *  Used by the chat-Undo button's out-of-window /
               *  cross-device path (`ai.revertAction({ eventId })`).
               *  Absent on pre-Slice-2 conversations. */
              eventId?: string | undefined;
            }
          | {
              kind: 'place.update';
              tripId: string;
              placeId: string;
              /** Previous values of only the fields the model patched —
               *  not the full place. Used by the server-side revert path
               *  to restore the pre-write state via `updatePlace`. */
              previousPatch: Record<string, unknown>;
              eventId?: string | undefined;
            }
          | {
              kind: 'transport.update';
              tripId: string;
              transportId: string;
              previousPatch: Record<string, unknown>;
              eventId?: string | undefined;
            }
          | {
              kind: 'custom.update';
              tripId: string;
              customId: string;
              previousPatch: Record<string, unknown>;
              eventId?: string | undefined;
            }
          | {
              kind: 'day.setMetadata';
              tripId: string;
              dayId: string;
              previousPatch: Record<string, unknown>;
              eventId?: string | undefined;
            }
          | {
              /** ADR-0084 Slice 6 — chat-Undo for batched writes.
               *  Carries the audit eventIds of every constituent
               *  action in input order. The client iterates them in
               *  REVERSE and calls ai.revertAction for each, so the
               *  composite undo restores the trip to its pre-batch
               *  state. Entries that are `null` mean the audit
               *  logger failed for that step — the client skips
               *  them and continues. (Wire shape is `string | null`,
               *  not `string | undefined`, because JSON arrays
               *  serialize `undefined` slots as `null`.) */
              kind: 'batch';
              tripId: string;
              eventIds: Array<string | null>;
            }
          | undefined;
        /** ADR-0084 Slice 3 Tier-B/C proposal handle. Set on the
         *  assistant message when the model called a `.propose`
         *  tool. The client renders a ProposalCard whose Accept /
         *  Discard buttons drive `ai.commitProposal` /
         *  `ai.discardProposal`. */
        proposal?:
          | {
              proposalId: string;
              preview: AssistantProposalPreview;
            }
          | undefined;
        /** ADR-0084 Slice 6 — number of constituent write actions
         *  inside a `batch.execute` call. Present only when
         *  `name === 'batch.execute'` and the batch committed. The
         *  per-conversation write cap counts each constituent action
         *  as one slot, so the seed in subsequent turns must sum this
         *  field (not count the batch as 1). Absent on every other
         *  toolCall entry. */
        batchStepCount?: number | undefined;
      }[]
    | undefined;
}

/** Structured preview shape rendered on the in-chat ProposalCard.
 *  Discriminated by `kind`; Slice 3 ships `places.add`. Subsequent
 *  slices append `customs.add`, `places.remove`, `places.move`,
 *  `customs.remove`. Field names match `proposal-service.ProposalPreview`
 *  exactly so the wire shape stays a one-to-one copy. */
export type AssistantProposalPreview =
  | {
      kind: 'places.add';
      tripId: string;
      dayId: string;
      dayLabel?: string;
      placeName: string;
      address?: string;
      startTime?: string;
      endTime?: string;
    }
  | {
      kind: 'customs.add';
      tripId: string;
      dayId: string;
      dayLabel?: string;
      title: string;
      subtitle?: string;
      startTime?: string;
      endTime?: string;
    }
  | {
      kind: 'places.remove';
      tripId: string;
      placeId: string;
      placeName: string;
    }
  | {
      kind: 'places.move';
      tripId: string;
      placeId: string;
      placeName: string;
      fromDayLabel?: string;
      toDayLabel?: string;
    }
  | {
      kind: 'customs.remove';
      tripId: string;
      customId: string;
      title: string;
    };

/** Result of `ai.sendMessage`. The non-error branch carries the newly-
 *  persisted assistant turn — the client can append it to local state
 *  immediately. Errors are typed so the UI can react (show upgrade card
 *  for QUOTA_EXCEEDED, retry chip for RATE_LIMITED, …). */
export type SendMessageResult =
  | { kind: 'reply'; message: AssistantMessage }
  | {
      kind: 'error';
      code:
        | 'AI_UNAVAILABLE'
        | 'QUOTA_EXCEEDED'
        | 'VAULT_READ_CAP'
        | 'RATE_LIMITED'
        | 'PROVIDER_ERROR'
        | 'TRIP_NOT_FOUND'
        | 'INPUT_INVALID';
      message: string;
    };

/** Wire input for `ai.sendMessage`. `lastSeenMessageId` is the cursor
 *  the client last received — when present, the server returns the
 *  delta of any messages persisted since (e.g. from another device)
 *  alongside the new reply, so each client converges. Optional; v1
 *  mobile UI doesn't use it yet. */
export const sendAssistantMessageInputSchema = z.object({
  tripId: z.string().min(1).max(255),
  text: z.string().trim().min(1).max(2000),
  lastSeenMessageId: z.string().min(1).max(40).optional(),
});
export type SendAssistantMessageInput = z.infer<typeof sendAssistantMessageInputSchema>;

/** Streaming variant — ADR-0082 slice 3.
 *
 *  Wire shape for `ai.streamMessage` (tRPC subscription over SSE).
 *  Two mid-stream event kinds plus the SendMessageResult union as the
 *  terminal:
 *
 *    - `token` — cumulative `text` snapshot of the assistant's
 *      cite_answer reply as it streams. The client renders this as
 *      the in-flight bubble body; each new snapshot replaces the
 *      previous render.
 *    - `tool_call` — { status: 'starting' | 'done' | 'error' } around
 *      each non-cite_answer dispatch. The mobile UI surfaces these
 *      as mid-thread "📄 Reading passport.pdf…" status chips per
 *      ADR-0082 §Q6.
 *    - Terminal: the existing `SendMessageResult` shape (`reply` or
 *      `error`) — clients reuse the same persistence + invalidation
 *      logic the non-streaming `sendMessage` mutation drives, just
 *      after the deltas land. */
export type AssistantStreamWireEvent =
  | { kind: 'token'; snapshot: string }
  | {
      kind: 'tool_call';
      toolCallId: string;
      name: string;
      status: 'starting' | 'done' | 'error';
    }
  | SendMessageResult;

/** Wire input for `ai.listConversation`. Cursor-paged on the
 *  composite sortKey `${tripId}#${createdAt}#${messageId}` — the
 *  trip id (UUID, 36 chars) + ISO 8601 timestamp (~24 chars) + a
 *  UUID message id (36 chars) sum to ~98 chars; the 200-char cap
 *  leaves headroom for the segment separators and any future
 *  embed (per-replica counter, etc.). */
export const listAssistantConversationInputSchema = z.object({
  tripId: z.string().min(1).max(255),
  limit: z.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type ListAssistantConversationInput = z.infer<
  typeof listAssistantConversationInputSchema
>;

/** Wire input for `ai.clearConversation`. */
export const clearAssistantConversationInputSchema = z.object({
  tripId: z.string().min(1).max(255),
});
export type ClearAssistantConversationInput = z.infer<
  typeof clearAssistantConversationInputSchema
>;

/** Wire input for `ai.revertAction` — ADR-0084. The client passes the
 *  audit eventId it received with the assistant message's revert
 *  payload; the server looks up the event, applies the inverse write,
 *  and flips `reverted: true` on the audit row. */
export const revertAssistantActionInputSchema = z.object({
  tripId: z.string().min(1).max(255),
  eventId: z.string().min(1).max(255),
});
export type RevertAssistantActionInput = z.infer<
  typeof revertAssistantActionInputSchema
>;

/** Wire result for `ai.revertAction`. */
export type RevertAssistantActionResult =
  | { kind: 'reverted' }
  | { kind: 'noop'; reason: 'ALREADY_REVERTED' }
  | {
      kind: 'error';
      code:
        | 'NOT_FOUND'
        | 'TRIP_NOT_FOUND'
        | 'UNSUPPORTED_ACTION'
        | 'PROVIDER_ERROR';
      message: string;
    };

/** Wire input for `ai.commitProposal` — ADR-0084 Slice 3. */
export const commitAssistantProposalInputSchema = z.object({
  tripId: z.string().min(1).max(255),
  proposalId: z.string().min(1).max(255),
});
export type CommitAssistantProposalInput = z.infer<
  typeof commitAssistantProposalInputSchema
>;

/** Wire input for `ai.discardProposal`. */
export const discardAssistantProposalInputSchema = z.object({
  tripId: z.string().min(1).max(255),
  proposalId: z.string().min(1).max(255),
});
export type DiscardAssistantProposalInput = z.infer<
  typeof discardAssistantProposalInputSchema
>;

/** Result of `ai.commitProposal`. `committed` is the success branch.
 *  `noop` covers the idempotency case where the user double-tapped
 *  Accept. `stale` carries the current entity state so the client can
 *  paraphrase "Things changed since I proposed this". */
export type CommitAssistantProposalResult =
  | { kind: 'committed' }
  | { kind: 'noop'; reason: 'ALREADY_DECIDED' }
  | {
      kind: 'error';
      code:
        | 'NOT_FOUND'
        | 'TRIP_NOT_FOUND'
        | 'STALE_PROPOSAL'
        | 'UNSUPPORTED_ACTION'
        | 'PROVIDER_ERROR';
      message: string;
    };

/** Result of `ai.discardProposal`. */
export type DiscardAssistantProposalResult =
  | { kind: 'discarded' }
  | { kind: 'noop'; reason: 'ALREADY_DECIDED' }
  | {
      kind: 'error';
      code: 'NOT_FOUND' | 'PROVIDER_ERROR';
      message: string;
    };
